import React, { useState, useEffect, useCallback } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faUser, faLock, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { verifyLogin } from "../services/authService";

import "./Login.css";

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    username: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);


  // ปลดล็อกเสียงเมื่อมี gesture ครั้งแรกในหน้า Login และบันทึกลง localStorage
  const markAudioUnlocked = useCallback(() => {
    try {
      localStorage.setItem('audioUnlocked', '1');
      // สร้าง AudioContext หนึ่งครั้งเพื่อกระตุ้น user activation (บางเบราว์เซอร์)
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        // ปิดทันทีเพื่อไม่กินทรัพยากร
        try { ctx.close(); } catch (_) {}
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const onFirstGesture = () => {
      markAudioUnlocked();
      document.removeEventListener('pointerdown', onFirstGesture);
      document.removeEventListener('keydown', onFirstGesture);
      document.removeEventListener('touchstart', onFirstGesture);
    };
    if (localStorage.getItem('audioUnlocked') !== '1') {
      document.addEventListener('pointerdown', onFirstGesture, { once: true });
      document.addEventListener('keydown', onFirstGesture, { once: true });
      document.addEventListener('touchstart', onFirstGesture, { once: true });
    }
    return () => {
      document.removeEventListener('pointerdown', onFirstGesture);
      document.removeEventListener('keydown', onFirstGesture);
      document.removeEventListener('touchstart', onFirstGesture);
    };
  }, [markAudioUnlocked]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: false,
    });
    setLoginStatus("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validate = () => {
    let isValid = true;
    const newErrors = { ...errors };

    if (!formData.username.trim()) {
      newErrors.username = true;
      isValid = false;
    }

    if (!formData.password.trim()) {
      newErrors.password = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validate()) {
      setIsLoading(true);
      setLoginStatus("กำลังตรวจสอบ...");

      try {
        const result = await verifyLogin(formData.username, formData.password);

        if (result.success) {
          // บันทึกข้อมูลการล็อกอินลง localStorage
          localStorage.setItem('user_credentials', JSON.stringify({
            username: formData.username,
            password: formData.password
          }));
          
          onLoginSuccess(result.data);
        } else {
          setLoginStatus(result.error || "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
          setIsLoading(false);
        }
      } catch (error) {
        console.error("เกิดข้อผิดพลาดในการ Login:", error);
        setLoginStatus("เกิดข้อผิดพลาดในการเชื่อมต่อ");
        setIsLoading(false);
      }
    } else {
      setLoginStatus("กรุณากรอกข้อมูลให้ครบถ้วน");
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>
      
      <div className="login-box">
        <div className="login-header">
          <div className="login-icon">
            <img src="/Wisdom-Logo.png" alt="Wisdom Logo" />
          </div>
          <h1 className="login-title">ยินดีต้อนรับ</h1>
          <p className="login-subtitle">เข้าสู่ระบบเพื่อเริ่มการเรียนรู้</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className={`input-group ${errors.username ? "error" : ""}`}>
            <div className="input-icon">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <input
              type="text"
              className="input-field"
              name="username"
              placeholder="Username"
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
              autocomplete="username"
            />
            {errors.username && <span className="error-message">กรุณากรอก Username</span>}
          </div>
          
          <div className={`input-group ${errors.password ? "error" : ""}`}>
            <div className="input-icon">
              <FontAwesomeIcon icon={faLock} />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              className="input-field"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              autocomplete="current-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={togglePasswordVisibility}
              disabled={isLoading}
              title={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
            {errors.password && <span className="error-message">กรุณากรอกรหัสผ่าน</span>}
          </div>
          
          <button 
            type="submit" 
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="spinner" />
                กำลังเข้าสู่ระบบ...
              </>
            ) : (
              'เข้าสู่ระบบ'
            )}
          </button>
          
          {loginStatus && (
            <div className={`login-status ${loginStatus.includes("สำเร็จ") ? "success" : "error"}`}>
              {loginStatus}
            </div>
          )}
        </form>
        
        <div className="login-footer">
          <p>© 2024 Wisdom Learning Platform</p>
        </div>
      </div>
    </div>
  );
};

export default Login;